/** Pad number to 2 digits. */
function pad(n) {
  return String(n).padStart(2, "0");
}

/**
 * Parse API UTC timestamps (Laravel created_at / updated_at).
 * Naive strings without timezone are treated as UTC.
 */
export function parseAsUtc(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const s = String(value).trim();
  if (!s) return null;

  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const normalized = s.replace(" ", "T");
  const withSeconds =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized) ? `${normalized}:00` : normalized;
  const d = new Date(`${withSeconds}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Parse naive local / wall-clock datetimes (e.g. crash_signals.date in America/Sao_Paulo).
 */
export function parseAsLocal(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const s = String(value).trim().replace(" ", "T");
  if (!s) return null;

  const normalized = s.length >= 16 ? s.substring(0, 19) : s;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format a Date as local `YYYY-MM-DDTHH:mm` for datetime-local inputs. */
export function toLocalInputValue(date = new Date()) {
  const d = date instanceof Date ? date : parseAsLocal(date);
  if (!d) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

/** Local datetime-local / wall value → API UTC `YYYY-MM-DD HH:mm` (for created_at filters). */
export function toApiUtc(localInput) {
  if (localInput == null || localInput === "") return "";
  const d = parseAsLocal(String(localInput).replace(" ", "T").substring(0, 16));
  if (!d) return "";
  return d.toISOString().substring(0, 16).replace("T", " ");
}

/** Local datetime-local value → API naive `YYYY-MM-DD HH:mm:ss` (for signal.date). */
export function toApiLocal(localInput) {
  if (localInput == null || localInput === "") return null;
  const s = String(localInput).trim().replace("T", " ");
  if (s.length === 16) return `${s}:00`;
  return s.substring(0, 19);
}

/** Display UTC API value in browser local time: `YYYY-MM-DD HH:mm:ss`. */
export function formatUtcAsLocal(value) {
  const d = parseAsUtc(value);
  if (!d) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}:${pad(d.getSeconds())}`;
}

/** Display UTC API value as local `HH:mm`. */
export function formatUtcAsLocalTime(value) {
  const d = parseAsUtc(value);
  if (!d) return "";
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Display UTC API value as local `DD HH:mm:ss` (compact table style). */
export function formatUtcAsLocalShort(value) {
  const formatted = formatUtcAsLocal(value);
  return formatted ? formatted.substring(8) : "";
}

/** Display naive local / signal date: `YYYY-MM-DD HH:mm`. */
export function formatLocalDateTime(value, withSeconds = false) {
  const d = parseAsLocal(value);
  if (!d) return value ? String(value).replace("T", " ").substring(0, withSeconds ? 19 : 16) : "";
  const base = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
  return withSeconds ? `${base}:${pad(d.getSeconds())}` : base;
}

/** Display naive local / signal date as `DD HH:mm:ss`. */
export function formatLocalShort(value) {
  const formatted = formatLocalDateTime(value, true);
  return formatted ? formatted.substring(8) : "";
}

/** Shift a Date by hours and return datetime-local value. */
export function localInputHoursFromNow(hours) {
  const d = new Date();
  d.setHours(d.getHours() + hours);
  return toLocalInputValue(d);
}
